import {
  HOP_PEAK_VELOCITY,
  MANTA_TEMPO,
  bankTarget,
  hopMs,
  hopProgress,
  hopVelocity,
  lowPass,
  tangentAt,
  type MantaState,
} from './mantaMotion';

/**
 * Moves a `MantaCharacter` along an SVG path (specs/012-manta-character/DESIGN.md §3, v2).
 *
 * The character sits in an HTML carrier layered over the stage SVG (not in a
 * `foreignObject`, which Chrome clips to its box whatever `overflow` says), so
 * the stage never repaints while it moves and nothing cuts a wingtip. One rAF
 * loop per hop: it samples the path, evaluates the C2 hop curve, low-passes the
 * bank, blends the hover offset and writes one transform. No layout reads.
 *
 * The driver is imperative on purpose: React owns the discrete state (rig state,
 * facing, look) and gets one callback per change; the per-frame work never
 * touches React.
 */
export interface DriverLayout {
  /** The character's width in stage (viewBox) units. */
  mantaW: number;
  /** Where the body's anchor sits relative to a stop it hovers over, in stage units. */
  hover: (facing: -1 | 1) => { dx: number; dy: number };
  /** Largest roll while banking (degrees). */
  maxBank?: number;
  /** Fade the roll to level through vertical tangents (needed where the facing flips). Default true. */
  fadeVertical?: boolean;
}

export interface DriverCallbacks {
  /** px per stage unit, read every frame (the stage is responsive). */
  scale: () => number;
  speed: () => number;
  reduced: () => boolean;
  onRig: (state: MantaState) => void;
  onFacing: (facing: -1 | 1) => void;
  onLook?: (look: { x: number; y: number } | null) => void;
}

export interface HopOptions {
  /** Duration at speed 1; defaults to `hopMs` by distance. */
  ms?: number;
  /** Switch the rig to `arrive` near the end (default true). */
  arrive?: boolean;
  /** Settle at the hover offset over the stop (default true); false lands on the path. */
  hover?: boolean;
  /** Called every frame with the path length reached (for reveals along the way). */
  onProgress?: (len: number, u: number) => void;
}

/** The body's centre of mass as fractions of the character's width (520/744, 330/744). */
const ANCHOR = { x: 520 / 744, y: 330 / 744 };
const FACING_DEADBAND = 0.2;

interface Hop {
  from: number;
  to: number;
  durationMs: number;
  t0: number;
  v0: number;
  offFrom: { dx: number; dy: number };
  offTo: { dx: number; dy: number };
  arrived: boolean;
  opts: HopOptions;
  resolve: () => void;
  lastNow: number;
}

export class MantaDriver {
  len = 0;
  /** Signed speed along the path in units per ms (positive = toward longer lengths). */
  vel = 0;
  bank = 0;
  facing: -1 | 1 = 1;
  private off = { dx: 0, dy: 0 };
  private pos = { x: 0, y: 0 };
  /** Unit tangent of the path at the character, in the direction it last moved. */
  private tan = { dx: 1, dy: 0 };
  private hop: Hop | null = null;
  private raf = 0;
  private total: number;
  private lookRaf = 0;
  private pointer: { x: number; y: number } | null = null;
  private lastLook: { x: number; y: number } | null = null;

  constructor(
    private readonly path: SVGPathElement,
    private readonly carrier: HTMLElement,
    private layout: DriverLayout,
    private readonly cb: DriverCallbacks,
  ) {
    this.total = path.getTotalLength();
  }

  setLayout(layout: DriverLayout) {
    this.layout = layout;
    this.total = this.path.getTotalLength();
  }

  get totalLength() {
    return this.total;
  }

  /**
   * v3: the path element's geometry was replaced by a fresh hop that starts where the
   * character is (the method stage builds one arc per hop). Stops the running hop without
   * resolving it and keeps the velocity, so the next `swimTo` carries the momentum on.
   */
  resetPath(len = 0) {
    this.finishHop(false);
    this.total = this.path.getTotalLength();
    this.len = Math.max(0, Math.min(this.total, len));
  }

  /** Where the body's anchor is, in stage units (path point plus hover offset). */
  anchor() {
    return { x: this.pos.x + this.off.dx, y: this.pos.y + this.off.dy };
  }

  /** Direction of travel while moving (unit vector in stage axes), null at rest. */
  heading(): { dx: number; dy: number } | null {
    return Math.abs(this.vel) > 1e-4 ? { ...this.tan } : null;
  }

  /** Put the character at `len` with no motion, hovering over it (or on the path). */
  place(len: number, hover = true) {
    this.cancel();
    this.len = Math.max(0, Math.min(this.total, len));
    this.vel = 0;
    this.bank = 0;
    const t = tangentAt(this.path, this.len, 1, this.total);
    this.pos = { x: t.x, y: t.y };
    if (Math.abs(t.dx) > FACING_DEADBAND) this.setFacing(t.dx < 0 ? -1 : 1);
    this.off = hover ? this.layout.hover(this.facing) : { dx: 0, dy: 0 };
    this.render();
  }

  /** Face a direction without moving (e.g. turn to look back at the rings). */
  face(facing: -1 | 1, rehover = true) {
    this.setFacing(facing);
    if (rehover) {
      this.off = this.layout.hover(this.facing);
      this.render();
    }
  }

  /** Swim to `toLen`. Resolves when the character has stopped. Interrupting a hop keeps its momentum. */
  swimTo(toLen: number, opts: HopOptions = {}): Promise<void> {
    const to = Math.max(0, Math.min(this.total, toLen));
    if (this.cb.reduced()) {
      this.place(to, opts.hover !== false);
      opts.onProgress?.(to, 1);
      return Promise.resolve();
    }
    const from = this.len;
    const distance = to - from;
    if (Math.abs(distance) < 0.5) {
      this.cancel();
      return Promise.resolve();
    }
    const durationMs = (opts.ms ?? hopMs(Math.abs(distance), this.layout.mantaW)) / this.cb.speed();
    // normalised launch speed: current velocity in hop lengths per hop duration
    const v0 = (this.vel * durationMs) / distance;
    const dir: 1 | -1 = distance > 0 ? 1 : -1;
    const end = tangentAt(this.path, to, dir, this.total);
    const facingAtEnd: -1 | 1 = Math.abs(end.dx) > FACING_DEADBAND ? (end.dx < 0 ? -1 : 1) : this.facing;
    this.finishHop(false);
    this.cb.onRig('swim');
    return new Promise<void>((resolve) => {
      this.hop = {
        from,
        to,
        durationMs,
        t0: performance.now(),
        lastNow: performance.now(),
        v0,
        offFrom: { ...this.off },
        offTo: opts.hover === false ? { dx: 0, dy: 0 } : this.layout.hover(facingAtEnd),
        arrived: false,
        opts,
        resolve,
      };
      this.raf = requestAnimationFrame(this.tick);
    });
  }

  /** Stop where it is (the current hop's promise resolves). */
  cancel() {
    this.finishHop(true);
  }

  destroy() {
    this.finishHop(false);
    if (this.lookRaf) cancelAnimationFrame(this.lookRaf);
  }

  /** The pointer in stage px relative to the stage's top-left, or null when it leaves. */
  setPointer(p: { x: number; y: number } | null) {
    this.pointer = p;
    if (!this.lookRaf) this.lookRaf = requestAnimationFrame(this.updateLook);
  }

  private tick = (now: number) => {
    const h = this.hop;
    if (!h) return;
    const dt = Math.min(64, now - h.lastNow);
    h.lastNow = now;
    const t = Math.min(1, (now - h.t0) / h.durationMs);
    const u = hopProgress(t, h.v0);
    const vN = hopVelocity(t, h.v0);
    const distance = h.to - h.from;
    this.len = h.from + distance * u;
    this.vel = (vN * distance) / h.durationMs;
    const dir: 1 | -1 = distance > 0 ? 1 : -1;
    const s = tangentAt(this.path, this.len, dir, this.total);
    this.pos = { x: s.x, y: s.y };
    this.tan = { dx: s.dx, dy: s.dy }; // tangentAt already points along the direction of travel
    if (Math.abs(s.dx) > FACING_DEADBAND) this.setFacing(s.dx < 0 ? -1 : 1);
    const target = bankTarget(s.dx, s.dy, Math.abs(vN) / HOP_PEAK_VELOCITY, {
      maxBank: this.layout.maxBank,
      fadeVertical: this.layout.fadeVertical,
      facing: this.facing,
    }).bank;
    this.bank = lowPass(this.bank, target, dt, MANTA_TEMPO.bankTauMs);
    this.off = {
      dx: h.offFrom.dx + (h.offTo.dx - h.offFrom.dx) * u,
      dy: h.offFrom.dy + (h.offTo.dy - h.offFrom.dy) * u,
    };
    this.render();
    h.opts.onProgress?.(this.len, u);
    if (!h.arrived && h.opts.arrive !== false && u >= MANTA_TEMPO.arriveAt) {
      h.arrived = true;
      this.cb.onRig('arrive');
    }
    if (t >= 1) {
      this.len = h.to;
      this.vel = 0;
      this.bank = 0;
      this.off = h.offTo;
      this.render();
      this.finishHop(true);
      return;
    }
    this.raf = requestAnimationFrame(this.tick);
  };

  private finishHop(resolve: boolean) {
    if (this.raf) cancelAnimationFrame(this.raf);
    this.raf = 0;
    const h = this.hop;
    this.hop = null;
    if (h && resolve) h.resolve();
  }

  private setFacing(f: -1 | 1) {
    if (f === this.facing) return;
    this.facing = f;
    this.cb.onFacing(f);
  }

  /** px position of the body's anchor on the stage. */
  anchorPx() {
    const s = this.cb.scale();
    return { x: (this.pos.x + this.off.dx) * s, y: (this.pos.y + this.off.dy) * s };
  }

  private render() {
    const s = this.cb.scale();
    const w = this.layout.mantaW * s;
    const x = (this.pos.x + this.off.dx) * s;
    const y = (this.pos.y + this.off.dy) * s;
    this.carrier.style.transform = `translate3d(${x.toFixed(2)}px, ${y.toFixed(2)}px, 0) rotate(${this.bank.toFixed(2)}deg) translate(${(-ANCHOR.x * w).toFixed(2)}px, ${(-ANCHOR.y * w).toFixed(2)}px)`;
  }

  private updateLook = () => {
    this.lookRaf = 0;
    if (!this.cb.onLook) return;
    let look: { x: number; y: number } | null = null;
    if (this.pointer) {
      const a = this.anchorPx();
      const w = this.layout.mantaW * this.cb.scale();
      const dx = this.pointer.x - a.x;
      const dy = this.pointer.y - a.y;
      const reach = w * 2.2;
      const dist = Math.hypot(dx, dy);
      if (dist <= reach && dist >= w * 0.25) look = { x: dx / reach, y: dy / reach };
    }
    const prev = this.lastLook;
    const same =
      (prev === null && look === null) ||
      (prev !== null && look !== null && Math.abs(prev.x - look.x) < 0.02 && Math.abs(prev.y - look.y) < 0.02);
    if (same) return;
    this.lastLook = look;
    this.cb.onLook(look);
  };
}
